import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { IntlModuleTypes, formatIntlMessage, useIntlModule } from '@enterprise/common';

import { UdsMessages } from '@utmost.co/utmost-design-system';
import messages from './uds.intl.json';

export const useUdsIntl = () => useIntlModule(messages);

export const useFormatUdsMessage = () => {
  const intlShape = useIntl();
  return (message: string, variables?: Record<string, string | number>) =>
    formatIntlMessage(intlShape, messages, message as IntlModuleTypes.IntlKeys<typeof messages>, variables);
};

export const useUdsMessages = (): UdsMessages => {
  const intl = useUdsIntl();

  return useMemo(
    (): UdsMessages => ({
      TransferList: {
        moveSelectedLeftBtn: {
          ariaLabel: intl.TransferListMoveSelectedLeftBtnAriaLabel
        },
        moveSelectedRightBtn: {
          ariaLabel: intl.TransferListMoveSelectedRightBtnAriaLabel
        },
        CheckboxList: {
          availableFields: intl.TransferListAvailableFields,
          chosenFields: intl.TransferListChosenFields,
          selected: 'TransferListSelectedFields'
        }
      },
      Tags: {
        exceedLimitError: 'TagsExceedLimitError',
        manageTagsTitle: intl.TagsManageTagsTitle,
        addNewTag: 'TagsAddNewTag',
        startTyping: intl.TagsStartTyping,
        noOptions: intl.TagsNoOptions
      },
      Select: {
        Chips: {
          endText: 'SelectChipsEndText'
        }
      },
      PasswordCheckList: {
        rules: {
          uppercase: {
            description: intl.PasswordCheckListRulesUppercaseDescription
          },
          lowerCase: {
            description: intl.PasswordCheckListRulesLowercaseDescription
          },
          numbers: {
            description: intl.PasswordCheckListRulesNumbersDescription
          },
          nonAlphanumeric: {
            description: intl.PasswordCheckListRulesNonAlphanumericDescription
          },
          length: {
            description: intl.PasswordCheckListRulesLengthDescription
          }
        }
      },
      OverFlowMenu: {
        noActions: {
          label: intl.OverFlowMenuNoActionsLabel
        }
      },
      Notification: {
        dismissButtonLabel: intl.NotificationDismissButtonLabel,
        markAllReadButtonLabel: intl.NotificationMarkAllReadButtonLabel
      },
      MenuPopper: {
        hintText: intl.MenuPopperHintText
      },
      Login: {
        Copyright: {
          text: 'LoginCopyRightText'
        },
        Separator: {
          or: intl.LoginSeparatorOr
        }
      },
      LocationMap: {
        locationName: {
          label: intl.LocationMapLocationNameLabel,
          placeholder: intl.LocationMapLocationNamePlaceholder
        },
        country: {
          label: intl.LocationMapCountryLabel,
          placeholder: intl.LocationMapCountryPlaceholder
        },
        addressLine1: {
          label: intl.LocationMapAddressLine1Label,
          placeholder: intl.LocationMapAddressLine1Placeholder
        },
        addressLine2: {
          label: intl.LocationMapAddressLine2Label,
          placeholder: intl.LocationMapAddressLine2Placeholder
        },
        cityTown: {
          label: intl.LocationMapCityTownLabel,
          placeholder: intl.LocationMapCityTownPlaceholder
        },
        zipPostalCode: {
          label: intl.LocationMapZipPostalCodeLabel,
          placeholder: intl.LocationMapZipPostalCodePlaceholder
        }
      },
      ImportErrorsDialog: {
        warningCopyText: intl.ImportErrorsDialogWarningCopyText,
        fatalCopyText: intl.ImportErrorsDialogFatalCopyText
      },
      AppForm: {
        submitButtonText: intl.AppFormSubmitButtonText,
        cancelButtonText: intl.AppFormCancelButtonText
      },
      FileUploadBox: {
        instructionHeader: intl.FileUploadBoxInstructionHeader
      },
      FileUploadDialog: {
        uploadBtn: intl.FileUploadDialogUploadBtn,
        cancelBtn: intl.FileUploadDialogCancelBtn
      },
      ExportDialog: {
        title: intl.ExportDialogTitle,
        exportBtn: intl.ExportDialogExportBtn,
        fileFormatInput: {
          label: intl.ExportDialogFileFormatInputLabel
        }
      },
      ErrorBoundary: {
        title: intl.ErrorBoundaryTitle,
        tryAgainBtn: intl.ErrorBoundaryTryAgainBtn
      },
      EmptyResults: {
        message: intl.EmptyResultsMessage
      },
      DocumentField: {
        uploadButtonLabel: intl.DocumentFieldUploadButtonLabel
      },
      Dialog: {
        ConfirmDialog: {
          cancelLabel: intl.DialogConfirmDialogCancelLabel,
          confirmLabel: intl.DialogConfirmDialogConfirmLabel
        },
        Dialog: {
          cancelLabel: intl.DialogDialogCancelLabel
        }
      },
      Chip: {
        ActiveInactiveStatusChip: {
          activeLabel: intl.ChipActiveInactiveStatusChipActiveLabel,
          inactiveLabel: intl.ChipActiveInactiveStatusChipInactiveLabel
        },
        EnabledDisabledStatusChip: {
          enabledLabel: intl.ChipEnabledDisabledStatusChipEnabledLabel,
          disabledLabel: intl.ChipEnabledDisabledStatusChipDisabledLabel,
          deletedLabel: intl.ChipEnabledDisabledStatusChipDeletedLabel
        },
        InputChips: {
          howManyLeftLabel: 'SelectChipsHowManyLeft'
        }
      },
      CheckboxTree: {
        loadingLabel: intl.CheckboxTreeLoadingLabel,
        noResultsLabel: intl.CheckboxTreeNoResultsLabel,
        unknownLabel: intl.CheckBoxTreeUnknownLabel
      },
      AccountSwitchingMenu: {
        title: intl.AccountSwitchingMenuTitle
      },
      CopyToClipboard: {
        tooltipText: { afterCopy: intl.CopyToClipboardAfterCopy, beforeCopy: intl.CopyToClipboardBeforeCopy }
      },
      DataTable: {
        ScheduleExport: {
          Form: {
            preferredTimeInput: {
              label: intl.DataTableScheduleExportFormPreferredTimeInputLabel,
              helperText: intl.DataTableScheduleExportFormPreferredTimeInputHelperText
            },
            timeIntervalInput: {
              label: intl.DataTableScheduleExportFormTimeIntervalInputLabel
            },
            startDateInput: {
              validation: {
                required: intl.DataTableScheduleExportFormStartDateInputValidationRequired
              },
              label: intl.DataTableScheduleExportFormStartDateInputLabel
            },
            fileFormatInput: {
              label: intl.DataTableScheduleExportFormFileFormatInputLabel
            }
          },
          CreateDialog: {
            title: intl.DataTableScheduleExportDialogTitle,
            btn: intl.DataTableScheduleExportDialogBtn,
            message: intl.DataTableScheduleExportDialogMessage
          },
          EditDialog: {
            title: intl.DataTableScheduleExportEditDialogTitle,
            btn: intl.DataTableScheduleExportEditDialogBtn,
            deleteBtn: intl.DataTableScheduleExportEditDialogDeleteBtn,
            message: intl.DataTableScheduleExportEditDialogMessage
          },
          DeleteDialog: {
            title: intl.DataTableScheduleDeleteExportDialogTitle,
            btn: intl.DataTableScheduleDeleteExportDialogBtn,
            message: intl.DataTableScheduleDeleteExportDialogMessage
          }
        },
        timeIntervals: {
          ANNUALLY: intl.DataTableTimeIntervalsAnnually,
          MONTHLY: intl.DataTableTimeIntervalsMonthly,
          WEEKLY: intl.DataTableTimeIntervalsWeekly,
          EVERY_4_HOURS: intl.DataTableTimeIntervalsEvery4Hours,
          DAILY: intl.DataTableTimeIntervalsDaily,
          BI_WEEKLY: intl.DataTableTimeIntervalsBiWeekly,
          NEVER: intl.DataTableTimeIntervalsNever
        },
        fileFormats: {
          CSV: intl.DataTableFileFormatsCSV,
          XLSX: intl.DataTableFileFormatsXLSX
        },
        ExportDialog: {
          label: intl.DataTableExportDialogLabel,
          fileFormatInput: {
            label: intl.DataTableExportDialogFileFormatInputLabel
          },
          withQuickView: {
            message: intl.DataTableExportDialogFileWithQuickViewMessage,
            noSchedule: {
              title: intl.DataTableExportDialogFileWithQuickViewNoScheduleTitle,
              message: intl.DataTableExportDialogFileWithQuickViewNoScheduleMessage,
              btn: intl.DataTableExportDialogFileWithQuickViewNoScheduleBtn
            },
            scheduled: {
              title: intl.DataTableExportDialogFileWithQuickViewScheduledTitle,
              editBtn: intl.DataTableExportDialogFileWithQuickViewScheduledEditBtn
            }
          }
        },
        BulkActions: {
          edit: intl.DataTableBulkActionsEdit,
          delete: intl.DataTableBulkActionsDelete,
          selected: {
            unknown: intl.DataTableBulkActionsSelectedUnknown,
            plural: 'DataTableBulkActionsSelectedPlural',
            singular: intl.DataTableBulkActionsSelectedSingular
          }
        },
        Filters: {
          Content: {
            quickViewSelected: {
              title: 'DataTableFiltersContentQuickViewSelectedTitle',
              manageBtn: intl.DataTableFiltersContentQuickViewSelectedManageBtn
            },
            unSavedQuickView: {
              title: intl.DataTableFiltersContentUnSavedQuickViewTitle,
              undoBtn: intl.DataTableFiltersContentUnSavedQuickViewUndoBtn
            }
          },
          filterLabel: intl.DataTableFiltersFilterLabel,
          filtersLabel: intl.DataTableFiltersFiltersLabel,
          allFiltersLabel: intl.DataTableFiltersAllFiltersLabel,
          relativeDateRange: {
            IS_ON: 'DataTableFiltersRelativeDateRange_IS_ON',
            IS_EMPTY: intl.DataTableFiltersRelativeDateRange_IS_EMPTY,
            IS_IN_BETWEEN: {
              FROM: 'DataTableFiltersRelativeDateRange_IS_IN_BETWEEN_FROM',
              TO: 'DataTableFiltersRelativeDateRange_IS_IN_BETWEEN_TO',
              FROM_TO: 'DataTableFiltersRelativeDateRange_IS_IN_BETWEEN_FROM_TO'
            },
            IS_IN_THE_LAST: {
              DAY: 'DataTableFiltersRelativeDateRange_IS_IN_THE_LAST_DAY',
              MONTH: 'DataTableFiltersRelativeDateRange_IS_IN_THE_LAST_MONTH',
              QUARTER: 'DataTableFiltersRelativeDateRange_IS_IN_THE_LAST_QUARTER',
              WEEK: 'DataTableFiltersRelativeDateRange_IS_IN_THE_LAST_WEEK',
              YEAR: 'DataTableFiltersRelativeDateRange_IS_IN_THE_LAST_YEAR'
            },
            IS_IN_THE_NEXT: {
              DAY: 'DataTableFiltersRelativeDateRange_IS_IN_THE_NEXT_DAY',
              MONTH: 'DataTableFiltersRelativeDateRange_IS_IN_THE_NEXT_MONTH',
              QUARTER: 'DataTableFiltersRelativeDateRange_IS_IN_THE_NEXT_QUARTER',
              WEEK: 'DataTableFiltersRelativeDateRange_IS_IN_THE_NEXT_WEEK',
              YEAR: 'DataTableFiltersRelativeDateRange_IS_IN_THE_NEXT_YEAR'
            },
            IS_WITHIN_THIS: 'DataTableFiltersRelativeDateRange_IS_WITHIN_THIS'
          },
          Current: {
            addNewFilterBtn: intl.DataTableFiltersCurrentAddNewFilterBtn,
            addNewFilterInput: {
              placeholder: intl.DataTableFiltersCurrentAddNewFilterInputPlaceholder
            }
          },
          Active: {
            search: 'DataTableFiltersActiveToolbarChipsSearchLabel',
            activeFilters: 'DataTableFiltersActiveToolbarChipsActiveLabel'
          },
          ActiveAndAvailable: {
            activeFilters: 'DataTableFiltersActiveAndAvailableActiveFilters',
            availableFilters: 'DataTableFiltersActiveAndAvailableAvailableFilters',
            clearAllFilters: intl.DataTableFiltersActiveAndAvailableClearAll
          }
        },
        QuickViews: {
          Panel: {
            repeats: 'DataTableQuickViewsPanelRepeats',
            next: 'DataTableQuickViewsPanelNext',
            noQuickViewsMessage: intl.DataTableQuickViewsPanelNoQuickViewsMessage
          },
          Drawer: {
            noQuickViewMessage: intl.DataTableQuickViewsDrawerNoQuickViewMsg,
            createQuickViewBtn: intl.DataTableQuickViewsDrawerCreateQuickViewBtn,
            pinnedQuickViewsLabel: intl.DataTableQuickViewsDrawerPinnedQuickViews,
            pinQuickViewsBtn: intl.DataTableQuickViewsDrawerPinQuickViewsBtn,
            unpinnedQuickViewsLabel: intl.DataTableQuickViewsDrawerUnpinnedQuickViews,
            unpinQuickViewsBtn: intl.DataTableQuickViewsDrawerUnpinQuickViewsBtn,
            unSavedBox: {
              action: intl.DataTableQuickViewsDrawerUnSavedBoxAction,
              description: intl.DataTableQuickViewsDrawerUnSavedBoxDescription,
              title: intl.DataTableQuickViewsDrawerUnSavedBoxTitle
            }
          },
          quickViews: intl.DataTableQuickViewsLabel,
          quickView: intl.DataTableQuickViewsQuickView,
          action: {
            edit: intl.DataTableQuickViewsActionEdit,
            delete: intl.DataTableQuickViewsActionDelete,
            save: intl.DataTableQuickViewsActionSave,
            saveAs: intl.DataTableQuickViewsActionSaveAs,
            duplicate: intl.DataTableQuickViewsActionDuplicate,
            pin: intl.DataTableQuickViewsActionPin,
            unpin: intl.DataTableQuickViewsActionUnpin,
            undoChanges: intl.DataTableQuickViewsActionUndoChanges,
            deleteScheduleExport: intl.DataTableQuickViewsActionDeleteScheduledExport,
            editScheduleExport: intl.DataTableQuickViewsActionEditScheduledExport,
            export: intl.DataTableQuickViewsActionExport,
            scheduleExport: {
              description: intl.DataTableQuickViewsActionScheduleExportDescription,
              label: intl.DataTableQuickViewsActionScheduleExportLabel
            },
            rename: intl.DataTableQuickViewsActionRename
          },
          DeleteQuickReportDialog: {
            title: intl.DataTableDeleteQuickReportDialogTitle,
            message: intl.DataTableDeleteQuickReportDialogMessage,
            deleteBtn: intl.DataTableDeleteQuickReportDialogDeleteBtn
          },
          AddQuickReportDialog: {
            saveBtn: intl.DataTableAddQuickReportDialogSaveBtn,
            nameInput: {
              label: intl.DataTableAddQuickReportDialogNameInputLabel
            },
            pinQuickViewCheckbox: {
              label: intl.DataTableAddQuickReportDialogPinQuickViewCheckboxLabel
            },
            title: {
              Add: intl.DataTableAddQuickReportDialogTitleAdd,
              Rename: intl.DataTableAddQuickReportDialogTitleRename,
              'Save as': intl.DataTableAddQuickReportDialogTitleSaveAs,
              Duplicate: intl.DataTableAddQuickReportDialogTitleDuplicate,
              Delete: intl.DataTableAddQuickReportDialogTitleDelete,
              Save: intl.DataTableAddQuickReportDialogTitleSave
            }
          }
        },
        SearchInput: {
          placeholder: intl.DataTableSearchInputPlaceholder,
          label: intl.DataTableSearchInputLabel
        },
        Sort: {
          tooltipText: intl.DataTableSortTooltipText
        },
        Dropdown: {
          tooltipText: intl.DataTableDropdownTooltipText
        },
        ColumnsConfiguration: {
          label: intl.DataTableColumnsConfigurationLabel,
          subTitle: intl.DataTableColumnsConfigurationSubTitle
        },
        ColumnSortPicker: {
          label: intl.DataTableColumnSortPickerLabel
        },
        ExportActions: {
          Export: {
            label: intl.DataTableExportActionsExportLabel
          }
        },
        Toolbar: {
          totalCount: 'DataTableToolbarTotalCountText',
          addButton: {
            tooltipText: intl.DataTableToolbarAddBtnTooltipText
          }
        },
        Error: {
          message: intl.DataTableErrorMessage,
          reloadBtn: intl.DataTableErrorReloadBtn,
          seeDetailsButton: intl.DataTableErrorSeeDetailsButton,
          hideDetailsButton: intl.DataTableErrorHideDetailsButton,
          copyErrorTooltipText: {
            beforeCopy: intl.DataTableErrorCopyErrorTooltipTextBeforeCopy
          }
        }
      }
    }),
    [intl]
  );
};
